// Notes Context with save status tracking, AI auto-tagging, and semantic search
import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getAllNotes, subscribeToNotes, saveNote, deleteNote, updateNoteTags } from '../services/firestore.js';
import { useAuth } from './AuthContext.jsx';
import { generateTags, isAIAvailable, semanticSearch } from '../services/ai-config.js';

const NotesContext = createContext(null);

export function NotesProvider({ children }) {
    const { user } = useAuth();
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedNote, setSelectedNote] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDomain, setSelectedDomain] = useState(null);
    const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
    const [semanticSearchEnabled, setSemanticSearchEnabled] = useState(false);
    const [semanticSearchResults, setSemanticSearchResults] = useState(null);
    const [semanticSearching, setSemanticSearching] = useState(false);

    // Load notes when user changes
    useEffect(() => {
        if (!user) {
            setNotes([]);
            setLoading(false);
            return;
        }

        setLoading(true);

        // Subscribe to real-time updates
        const unsubscribe = subscribeToNotes(user.uid, (updatedNotes) => {
            setNotes(updatedNotes);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [user]);

    // Keep selectedNote in sync with notes array when remote updates occur
    // This is critical for real-time sync across devices
    useEffect(() => {
        if (!selectedNote) return;

        // Find the corresponding note in the updated notes array
        const updatedNote = notes.find(n => n.key === selectedNote.key);

        if (!updatedNote) {
            // Note was deleted remotely
            setSelectedNote(null);
            return;
        }

        // Check if content actually changed (compare by content, not by reference)
        if (updatedNote.content !== selectedNote.content ||
            updatedNote.updatedAt !== selectedNote.updatedAt ||
            JSON.stringify(updatedNote.tags) !== JSON.stringify(selectedNote.tags)) {
            // Update selectedNote with the new data from real-time sync
            setSelectedNote(updatedNote);
        }
    }, [notes, selectedNote]);

    // Filter notes based on search and domain
    const filteredNotes = useMemo(() => {
        // If semantic search is active and has results, use those
        if (semanticSearchEnabled && semanticSearchResults) {
            return semanticSearchResults.filter(note => {
                if (selectedDomain && note.domain !== selectedDomain) return false;
                return true;
            });
        }

        return notes.filter(note => {
            if (selectedDomain && note.domain !== selectedDomain) {
                return false;
            }

            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const content = getPlainTextContent(note.content).toLowerCase();
                const key = (note.key || '').toLowerCase();
                const tags = (note.tags || []).join(' ').toLowerCase();
                return content.includes(query) || key.includes(query) || tags.includes(query);
            }

            return true;
        });
    }, [notes, selectedDomain, searchQuery, semanticSearchEnabled, semanticSearchResults]);

    // Get unique domains
    const domains = [...new Set(notes.map(n => n.domain).filter(Boolean))].sort();

    // Get unique tags from all notes
    const allTags = [...new Set(notes.flatMap(n => n.tags || []))].sort();

    // Track pending tag generations to avoid duplicates
    const pendingTagsRef = useRef(new Set());

    // Save note with status tracking and background auto-tagging
    // metadata parameter is optional and can include { folderId, tagIds }
    const updateNote = useCallback(async (key, content, metadata = null) => {
        if (!user) return;

        setSaveStatus('saving');
        try {
            await saveNote(user.uid, key, content, null, metadata);
            setSaveStatus('saved');
            // Reset to idle after 2 seconds
            setTimeout(() => setSaveStatus('idle'), 2000);

            // Background auto-tagging (non-blocking)
            // Only trigger if AI is available and not already pending for this key
            // Skip if user manually assigned tags (tagIds provided in metadata)
            if (isAIAvailable() && !pendingTagsRef.current.has(key) && !metadata?.tagIds?.length) {
                pendingTagsRef.current.add(key);

                // Debounce - wait 2 seconds to avoid tagging on every keystroke
                setTimeout(async () => {
                    try {
                        // Get plain text content for tagging
                        const plainText = getPlainTextContent(content);
                        if (plainText && plainText.length >= 50) {
                            const tags = await generateTags(plainText);
                            if (tags && tags.length > 0) {
                                await updateNoteTags(user.uid, key, tags);
                            }
                        }
                    } catch (tagError) {
                        console.warn('Auto-tagging failed:', tagError);
                        // Silent fail - don't interrupt user workflow
                    } finally {
                        pendingTagsRef.current.delete(key);
                    }
                }, 2000);
            }
        } catch (error) {
            console.error('Failed to save note:', error);
            setSaveStatus('error');
        }
    }, [user]);

    // Force sync/refresh notes
    const syncNotes = useCallback(async () => {
        if (!user) return;

        setLoading(true);
        try {
            const freshNotes = await getAllNotes(user.uid);
            setNotes(freshNotes);
        } catch (error) {
            console.error('Failed to sync notes:', error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    // Delete note
    const removeNote = useCallback(async (key) => {
        if (!user) return;
        await deleteNote(user.uid, key);
        if (selectedNote?.key === key) {
            setSelectedNote(null);
        }
    }, [user, selectedNote]);

    // Select note
    const selectNote = useCallback((note) => {
        setSelectedNote(note);
    }, []);

    // Perform semantic AI search
    const performSemanticSearch = useCallback(async (query) => {
        if (!query || query.trim().length < 3 || notes.length === 0) {
            setSemanticSearchResults(null);
            return;
        }

        setSemanticSearching(true);
        try {
            const results = await semanticSearch(query, notes);
            setSemanticSearchResults(results);
        } catch (error) {
            console.error('Semantic search failed:', error);
            setSemanticSearchResults(null);
        } finally {
            setSemanticSearching(false);
        }
    }, [notes]);

    // Clear semantic search results when search is cleared or mode toggled off
    const clearSemanticSearch = useCallback(() => {
        setSemanticSearchResults(null);
    }, []);

    const value = {
        notes: filteredNotes,
        allNotes: notes,
        loading,
        selectedNote,
        searchQuery,
        selectedDomain,
        domains,
        allTags,
        saveStatus,
        semanticSearchEnabled,
        semanticSearching,
        setSearchQuery,
        setSelectedDomain,
        selectNote,
        updateNote,
        removeNote,
        syncNotes,
        setSemanticSearchEnabled,
        performSemanticSearch,
        clearSemanticSearch
    };

    return (
        <NotesContext.Provider value={value}>
            {children}
        </NotesContext.Provider>
    );
}

export function useNotes() {
    const context = useContext(NotesContext);
    if (!context) {
        throw new Error('useNotes must be used within a NotesProvider');
    }
    return context;
}

// Helper to get plain text from note content
function getPlainTextContent(content) {
    if (!content) return '';

    if (typeof content === 'string') {
        try {
            const parsed = JSON.parse(content);
            if (parsed.version === 2 && parsed.content) {
                return extractTextFromDoc(parsed.content);
            }
            if (parsed.type === 'doc') {
                return extractTextFromDoc(parsed);
            }
        } catch {
            return content;
        }
    }

    return '';
}

function extractTextFromDoc(doc) {
    if (!doc || !doc.content) return '';

    let text = '';
    for (const node of doc.content) {
        text += extractTextFromNode(node) + '\n';
    }
    return text.trim();
}

function extractTextFromNode(node) {
    if (node.type === 'text') {
        return node.text || '';
    }
    if (node.content) {
        return node.content.map(extractTextFromNode).join('');
    }
    return '';
}
